import type { HelpEntry } from "./help.ts";

export const CATEGORIES_EN = [
  "Open and create projects",
  "The map",
  "Editing the project",
  "Models",
  "Calculate",
  "Results",
  "Other",
] as const;

export const HELP_EN: HelpEntry[] = [
  // --- Open and create projects ---
  {
    id: "abrir-proyecto",
    category: "Open and create projects",
    title: "Open an existing project",
    keywords: ["folder", "ehd", "chrome", "edge", "showdirectorypicker"],
    content: [
      '"Open" button on the start screen: choose the folder that contains the .ehd file and, alongside it, the folder with the input series (precipitation, ETP...). You need a browser with File System Access API support — Chrome or Edge, not Firefox or Safari.',
      "If the folder has no .ehd file but does have the CSV files (sub-basins, zones, parameters...), it's detected and opened from those instead, no need to create a .ehd by hand.",
    ],
  },
  {
    id: "formatos-proyecto",
    category: "Open and create projects",
    title: "Old and new models (with zones)",
    keywords: ["no zones", "old", "legacy", "compatibility"],
    content: [
      "They open the same way, without you having to indicate anything: older models (from before the Zone concept existed) and current ones (with Zones) are detected automatically when reading the .ehd.",
      "In an old-style model, each sub-basin automatically becomes its own zone when opened — so the rest of the app (editing, calculation...) works exactly the same way in both cases.",
    ],
  },
  {
    id: "formatos-series",
    category: "Open and create projects",
    title: "Precipitation, ETP, temperature and pumping formats",
    keywords: ["csv", "inp", "series", "input", "files", "file format"],
    content: [
      "There are also two formats, depending on the model, and they're also detected automatically from the file's content (no need to say which one it is): a CSV separated by \";\" with the date in dd/mm/yyyy (models with Zones), or an older format with tagged blocks and tab-separated columns (old-style models, .inp files).",
      "In the old format, columns are linked to each sub-basin by a column number stored in the project, not by name — the opposite of the new format.",
    ],
  },
  {
    id: "crear-proyecto",
    category: "Open and create projects",
    title: "Create a new project from scratch",
    keywords: ["wizard", "new", "shapefile", "step by step"],
    content: [
      '"Create new" button on the start screen. The wizard walks you through it step by step: sub-basins (imported from a shapefile, or created by hand if you don\'t have one), zones (each with a model and its parameters), outlet points and aquifers, the topology (which sub-basin drains into which outlet/aquifer), and finally the precipitation/ETP series.',
      'If you\'d rather not go through the whole wizard, you can create a minimal project and add the rest later from "Edit project".',
    ],
  },
  {
    id: "ejemplos",
    category: "Open and create projects",
    title: "Try an example",
    keywords: ["turia", "demo", "test", "with gis", "without gis"],
    content: [
      '"With GIS"/"Without GIS" buttons on the start screen: load a real example project (77 sub-basins from the Turia) without uploading anything of your own, to explore the app before using your own data — one with the geographic map, the other with the card view.',
      "Changes to an example aren't saved on their own (you'll see a persistent notice at the bottom while you work) — use \"Save copy as…\" at any time to turn it into a real project of your own, with normal autosaving from then on.",
    ],
  },
  {
    id: "tus-proyectos",
    category: "Open and create projects",
    title: "Your projects, saved automatically",
    keywords: ["saved", "autosave", "reopen", "list", "indexeddb", "rename"],
    content: [
      '"Your projects" section on the start screen (only visible once you have at least one): any project opened from a folder or created with the wizard autosaves in this browser — click to reopen it exactly as you left it, without going through the original folder again.',
      "Hover over a project in the list to see the rename pencil, inside its own row.",
    ],
  },
  {
    id: "importar-copia",
    category: "Open and create projects",
    title: "Import a copy (.ehdz)",
    keywords: ["ehdz", "import", "file", "another computer", "another browser"],
    content: [
      '"Import copy…" button on the start screen: opens a .ehdz file exported earlier (see "Export a portable copy (.ehdz)") and adds it as a new, genuinely saved project — useful for moving a project to another browser or computer, without the original folder.',
    ],
  },
  {
    id: "proyectos-con-o-sin-gis",
    category: "Open and create projects",
    title: "Projects with or without GIS",
    keywords: ["shapefile", "geometry", "optional", "no map"],
    content: [
      "GIS (the shapefile with the real shape of the sub-basins) is optional throughout the app, both when opening a real project and when creating a new one: if you don't have it, or the project doesn't come with a linkable one, the app works the same, just using the card view instead of the geographic map (see \"Map or card view\").",
      'You can add or link a shapefile later at any time (Edit project → "GIS layer" tab), and you can also draw sub-basins by hand directly on the map without needing any file (see "Draw sub-basins, outlet points and aquifers by hand").',
    ],
  },

  // --- The map ---
  {
    id: "vista-mapa-tarjetas",
    category: "The map",
    title: "Map or card view",
    keywords: ["cards", "toggle", "switch view", "no geometry"],
    content: [
      "View icon (top right, next to the edit pencil): switches between the geographic map and a schematic view with sub-basins as cards. The card view works the same even if the project doesn't have any geometry yet.",
    ],
  },
  {
    id: "colores-mapa",
    category: "The map",
    title: "What the sub-basin colors mean",
    keywords: ["gray", "style", "legend", "issue", "snow", "stripes", "unused"],
    content: [
      "Teal: normal, ready to be calculated. Gray: something's missing (zone, outlet, its own input data...) and it will be left out of the calculation until it's completed.",
      "A sub-basin with a snow zone is marked with a diagonal stripe pattern over its same color (not a new color). An outlet point or aquifer that no sub-basin uses is shown in light yellow (different from the gray notice color).",
      "Legend icon (top left of the map) explains all these colors/patterns at a glance — it can be hidden and reopened without losing your position on the map.",
    ],
  },
  {
    id: "capas-base-mapa",
    category: "The map",
    title: "Change the background map",
    keywords: [
      "satellite",
      "aerial view",
      "terrain",
      "streets",
      "layers",
      "official basins",
      "official sub-basins",
      "miteco",
      "background",
    ],
    content: [
      "Layers icon (top right of the map): choose between three backgrounds — Streets, Aerial view and Terrain.",
      "From the same selector you can also overlay two MITECO reference layers — Spain's official basins and sub-basins — with no relation to your project's data, just for visual comparison. Right-click a polygon in either to add it as a sub-basin of your project.",
    ],
  },
  {
    id: "editar-con-clic",
    category: "The map",
    title: "Edit a sub-basin, outlet point or aquifer with a click",
    keywords: ["direct click", "open dialog", "no pencil", "cards"],
    content: [
      "Outside of any special mode (multi-selection, results, drawing), clicking an element on the map or in the card view opens its edit dialog directly — no need to go through the pencil or any catalog just to see or change its data.",
      "The pencil is reserved for multi-selection (assigning a zone/outlet/aquifer or deleting in bulk) and for the catalogs (Zones, GIS layer, Draw...).",
    ],
  },
  {
    id: "dibujar-mapa",
    category: "The map",
    title: "Draw sub-basins, outlet points and aquifers by hand",
    keywords: ["drawing", "polygon", "vertex", "point", "click", "finish"],
    content: [
      'Edit project → "Draw" tab, choose what you want to create and draw directly on the base map: a click places an outlet point; for a sub-basin or an aquifer, several clicks place vertices and "Finish" closes the outline (minimum 3, no maximum limit or shape restriction).',
      "Once the geometry is finished you're asked for a name (and, for a sub-basin, the area — calculated automatically from the drawing, but editable).",
    ],
  },
  {
    id: "vincular-shapefile",
    category: "The map",
    title: "Link or replace the sub-basin shapefile",
    keywords: ["shp", "dbf", "prj", "gis layer", "link"],
    content: [
      'Edit project → "GIS layer" tab: upload a shapefile (.shp + .dbf + .prj) and choose which column identifies each sub-basin, for when the project doesn\'t come with a linkable one or you want to change it.',
    ],
  },

  // --- Editing the project ---
  {
    id: "seleccionar-subcuencas",
    category: "Editing the project",
    title: "Select sub-basins",
    keywords: ["shift", "drag", "rectangle", "selection"],
    content: [
      "With the edit panel open: clicking a sub-basin adds or removes it from the selection; Shift + drag selects everything the rectangle touches (only available in map view).",
    ],
  },
  {
    id: "asignar-en-bloque",
    category: "Editing the project",
    title: "Assign a zone, outlet point or aquifer to several sub-basins at once",
    keywords: ["bulk", "apply", "mass assignment"],
    content: [
      'With one or more sub-basins selected, the edit panel shows a dropdown + "Apply" button for each assignable thing (zone, snow zone, outlet point, aquifer) — it applies to the whole selection at once.',
    ],
  },
  {
    id: "eliminar-permanente",
    category: "Editing the project",
    title: "Delete a sub-basin, zone, outlet point or aquifer",
    keywords: ["trash", "undo", "recover", "delete", "permanent"],
    content: [
      "Deleting is final: it asks for confirmation because there's no undo. If the element came from a GIS layer, the only way to recover it is to re-import or redraw that geometry — it's created as new, without the configuration it had before (zone, topology, settings...), so you'll need to reconfigure it.",
      'For sub-basins there\'s a shortcut: in Edit project → "GIS layer", uploading the original full shapefile again shows "Recover deleted sub-basins" — it compares the file\'s geometries with the ones already in the project and only offers to add the ones that are missing, without duplicating the ones that were never deleted.',
    ],
  },
  {
    id: "zonas",
    category: "Editing the project",
    title: "What a zone is",
    keywords: ["parameters", "share", "create zone", "change model"],
    content: [
      'A zone groups the parameters of a model (rainfall-runoff or snow) that several sub-basins can share, instead of calibrating each sub-basin separately. They\'re created and edited from Edit project → "Zones" tab.',
      "The model of an already-created zone can be changed later — doing so resets its parameters to the new model's defaults.",
    ],
  },
  {
    id: "editar-subcuenca",
    category: "Editing the project",
    title: "Edit a sub-basin's name, area or adjustments",
    keywords: ["name", "area", "precipitation adjustment", "etp adjustment", "basic data"],
    content: [
      'Click the sub-basin (map or card, see "Edit a sub-basin, outlet point or aquifer with a click") to open its edit dialog. The "Basic data" tab has the name, area and the precipitation/ETP/temperature multiplier adjustments for that specific sub-basin; the "Series" tab has its input data (see "Paste or view the series of a single sub-basin/aquifer").',
    ],
  },
  {
    id: "pegar-serie-individual",
    category: "Editing the project",
    title: "Paste or view the series of a single sub-basin/aquifer",
    keywords: ["excel", "paste", "statistics", "gaps", "mean", "max", "min", "gap", "chart"],
    content: [
      'Inside a sub-basin\'s edit dialog ("Series" tab) or an aquifer\'s, "Paste series…" accepts two columns copied from a spreadsheet (date dd/mm/yyyy + value) — a finer-grained alternative to uploading the whole file when you only need to fix or complete one element\'s data.',
      "If that series already existed in the project, the pasted dates have to match its dates exactly; otherwise it's rejected, showing the expected range, instead of mixing two date ranges without you noticing.",
      "Next to the chart you'll see the date range, the gaps detected in the calendar, and the series' mean/max/min.",
    ],
  },
  {
    id: "obtener-de-aemet",
    category: "Editing the project",
    title: "Fetching precipitation, temperature and ETP from AEMET automatically",
    keywords: [
      "aemet",
      "station",
      "temperature",
      "etp",
      "hargreaves",
      "idw",
      "method",
      "nearest station",
      "automatic download",
    ],
    content: [
      'The "Fetch from AEMET" button (a sub-basin\'s "Series" tab) fills in precipitation, temperature and ETP from the AEMET weather stations nearest that sub-basin\'s centroid, with nothing to upload or paste by hand — "Fetch AEMET series" in Edit project → "Input series" does the same for several sub-basins at once.',
      "The temperature that gets saved is the daily mean the station recorded that day (tmed) — not the max or min. ETP (potential evapotranspiration) is calculated with the Hargreaves-Samani formula (FAO-56): it does use that day's max and min temperature, along with the mean and the sub-basin's latitude, but those two values never get saved as a series of their own — they're only used internally for that calculation.",
      'Three methods for combining a sub-basin\'s candidate stations or grid cells: "Nearest station" (each day uses the nearest one with data, falling back to the next if that one is missing), "Distance-weighted average (IDW)" — blends every station with data that day, weighting the closer ones more; tends to do better when the primary station is a bit far or the terrain is complex — or "ROCIO grid" — uses EVERY AEMET grid cell (5 km, 1951-2022) that falls inside the sub-basin, averaging precipitation and taking the min/max for temperature; this is the method the MITECO wizard\'s automatic download now uses by default, with its full historical range instead of just a few years. The method can be chosen by hand, both per sub-basin and for a bulk download.',
    ],
  },
  {
    id: "series-de-entrada-panel",
    category: "Editing the project",
    title: "Upload or replace input series on an already-open project",
    keywords: ["csv", "link series", "input series", "replace", "reload"],
    content: [
      'Edit project → "Input series" tab: upload or replace any of the 4 series (precipitation/ETP mandatory, temperature/pumping optional) on the already-open project, at any time — useful for completing a precipitation/ETP series that was missing when you opened it, without having to recreate the project.',
    ],
  },
  {
    id: "calibracion-parametros",
    category: "Editing the project",
    title: "Automatically calibrating a zone's parameters",
    keywords: [
      "calibration",
      "calibrate",
      "sce-ua",
      "nash",
      "nse",
      "optimization",
      "observed series",
      "observed flow",
    ],
    content: [
      "To use it, contact info@fluit.es.",
      "Automatically tunes some of a zone's parameters so the simulated contribution at an outlet point matches a real observed flow as closely as possible, instead of trying values by hand — uses the same algorithm (SCE-UA) as EvalHid's desktop calibrator.",
      'It first needs an observed series: open it from the outlet point itself (click it on the map or its card) while the Calibration panel is open — only then does its "Observed series" tab appear, with the same upload/paste/delete options as the other series.',
      "In the panel: pick the outlet point (only ones that already have an observed series show up), the zone to calibrate (only ones draining into that point with a model assigned), and which of that zone's parameters to adjust — each with a fixed physical range, not editable.",
      "It also sets two independent date periods: a calibration one (where parameters actually get adjusted) and a validation one (to check the result out-of-sample, not a second report on the same period) — plus an optional warm-up period (duration in days/weeks/months/years) that extends the simulation backward without scoring it, so each evaluation doesn't start from the model's empty storages.",
      'While calibrating you\'ll see a live chart of the objective function improving, and once it finishes, a table with 6 goodness-of-fit indicators (Nash-Sutcliffe among them) before/after — "Apply these parameters" only writes them into the project if you confirm, never silently.',
      "The observed series has to be in the same units as the project's aggregated result (shown next to the picker) — calibration doesn't detect or correct a unit mismatch.",
    ],
  },

  // --- Models ---
  {
    id: "modelos-lluvia-escorrentia",
    category: "Models",
    title: "Available rainfall-runoff models",
    keywords: ["temez", "sac", "hbv", "gr2m", "gr4j", "awbm"],
    content: [
      "Témez, SAC, HBV, GR2M, GR4J and AWBM. Each zone uses one of these six, with its own calibratable parameters.",
    ],
  },
  {
    id: "modelos-nieve",
    category: "Models",
    title: "Snow models (optional)",
    keywords: ["snow1", "snow2", "melt", "snowpack"],
    content: [
      "Nieve1 and Nieve2: optional, assigned to a separate snow zone, and if a sub-basin has one, it's applied before the rainfall-runoff model (the liquid precipitation that comes out of the snow model is what feeds into the other model).",
    ],
  },
  {
    id: "parametros-defecto",
    category: "Models",
    title: "Default parameters for each model",
    keywords: ["factory values", "template", "calibrate"],
    content: [
      "Each model comes with reasonable default values when you create a new zone, editable one by one afterward — they're a starting point for calibration, not a final value.",
    ],
  },

  // --- Calculate ---
  {
    id: "calcular",
    category: "Calculate",
    title: "Calculate the contribution",
    keywords: ["dates", "calculate button", "pumping", "negative aquifers", "model pumping"],
    content: [
      '"Calculate" button: opens a dialog to choose the date range (within what the loaded precipitation covers) and two aquifer options ("Negative aquifer values" and "Model pumping") before running the calculation.',
    ],
  },
  {
    id: "avisos-bloqueantes",
    category: "Calculate",
    title: "What notices and blockers are",
    keywords: ["bell", "notifications", "error", "can't calculate", "failure"],
    content: [
      "A blocker prevents calculating entirely (e.g. the precipitation series hasn't been loaded) — the \"Calculate\" button stays disabled until it's resolved. A notice doesn't block anything, it just informs you of something to keep in mind (e.g. sub-basins without their own input data, which are excluded from the calculation but don't stop the rest from being calculated). Both show up under the bell icon.",
      "If the calculation itself fails (an uncovered edge case, inconsistent data), it shows up right there as a red 🔴 notice, distinct from the others — the rest of the project and the map stay intact, only that calculation attempt fails.",
    ],
  },
  {
    id: "simulacion-parcial",
    category: "Calculate",
    title: "Partial simulation: sub-basins without their own data",
    keywords: ["excluded", "missing data", "no own data"],
    content: [
      "If some sub-basins are missing their own precipitation/ETP data (or temperature, if they use a snow model), the calculation isn't blocked for the whole project: the ones that do have data get calculated, and you're explicitly told which ones were left out and why.",
      "The same happens if a sub-basin's precipitation or ETP has an empty or non-numeric cell within the calculated range (not a whole missing day, but a single missing value inside a day that's otherwise present): that sub-basin is excluded from the result just as if it had no data, with a separate notice after calculating showing which one and the exact dates with the issue.",
    ],
  },
  {
    id: "huecos-fecha-bloqueante",
    category: "Calculate",
    title: "Date gaps in the series: why they block the calculation",
    keywords: ["gap", "missing date", "missing day", "blocker", "calendar"],
    content: [
      "If the precipitation, ETP, temperature or pumping series is missing a whole calendar day outright (not an empty value inside a day that's present, but the entire day absent from the table), the calculation is blocked for the whole project, not just the affected sub-basins — the notice shows the exact missing dates.",
      "The only way to fix it is to correct the input file (add the missing row) and upload it again — unlike an empty cell inside a day that's present (see \"Partial simulation: sub-basins without their own data\"), the app can't work around a date gap by just excluding the affected sub-basin.",
    ],
  },
  {
    id: "recalcular",
    category: "Calculate",
    title: "Recalculate without repeating the dialog",
    keywords: ["recalculate", "arrows icon"],
    content: [
      'Once you\'ve calculated at least once, a recalculate icon appears next to the "Calculate" button that repeats the calculation with the same options (dates, aquifers...) without asking again.',
    ],
  },

  // --- Results ---
  {
    id: "panel-resultados",
    category: "Results",
    title: "The results panel",
    keywords: [
      "chart",
      "table",
      "annual contribution",
      "hm3",
      "outlet point",
      "zone",
      "units",
      "aquifer",
    ],
    content: [
      'The main result is the "Contribution by outlet point" — the sum of every sub-basin draining into each point, not a per-sub-basin series. There are tabs to view it by Zone (grouped), by Sub-basin (diagnostic, not aggregated), and by Aquifer too (each aquifer\'s own balance — recharge, pumping and total — with no surface/underground breakdown, since an aquifer has no surface component of its own).',
      "A selector in the panel header changes the units (e.g. Hm³) on the fly, without recalculating.",
      "Which sub-basin/outlet point to view can also be chosen by clicking directly on the map, while the Results panel is open (the Zone has no geometry of its own, so it can only be chosen from the list).",
    ],
  },
  {
    id: "detalle-interno",
    category: "Results",
    title: 'View a sub-basin\'s internal detail ("partial results")',
    keywords: ["view detail", "moisture", "internal series", "select series"],
    content: [
      "\"View detail\" button in a sub-basin's row: shows the model's internal step-by-step series (soil moisture, runoff, base flow, snow content...) with a selector to choose which ones to overlay on the same chart.",
    ],
  },
  {
    id: "descargar-csv",
    category: "Results",
    title: "Download results as CSV",
    keywords: ["export", "csv", "download"],
    content: [
      '"Download CSV" button (in the general results panel, and also inside each sub-basin\'s detail): generates a text file with the calculated values, ready to open in a spreadsheet.',
    ],
  },

  // --- Other ---
  {
    id: "tema-oscuro",
    category: "Other",
    title: "Dark / light mode",
    keywords: ["theme", "moon", "sun", "dark mode"],
    content: [
      "Gear icon (Settings, at the very top right, and also on the start screen): inside, a moon/sun button switches the theme. The preference is remembered between sessions in this same browser.",
    ],
  },
  {
    id: "idioma",
    category: "Other",
    title: "Change the language",
    keywords: ["language", "english", "spanish", "es", "en"],
    content: [
      "Inside Settings (gear icon, top right, and also on the start screen): the language you're currently viewing is always highlighted, no need to guess which one to pick.",
      "With nothing chosen yet, the app starts in your browser's language (English if your browser is in English, Spanish in any other case) — once you pick one by hand, that choice is remembered between sessions in this same browser and no longer depends on the browser.",
    ],
  },
  {
    id: "persistencia",
    category: "Other",
    title: "Is my work saved?",
    keywords: [
      "save",
      "persistence",
      "memory",
      "close tab",
      "lose changes",
      "autosave",
      "indexeddb",
    ],
    content: [
      'Yes, automatically: every change is saved in this browser (IndexedDB) a second after you stop typing/editing, with no save button. You can close the tab or reload the page without losing anything — when you reopen the app, the project is still in "Your projects" exactly as you left it.',
      'The only exception is the examples ("With GIS"/"Without GIS"): until you do "Save copy as…", their changes don\'t persist between sessions (see "Try an example").',
      "The original folder of a project opened from disk is never touched again after opening it — you can move or delete it without affecting what's already saved in the browser.",
    ],
  },
  {
    id: "sincronizacion-drive",
    category: "Other",
    title: "Sync your projects with Google Drive",
    keywords: [
      "drive",
      "google",
      "sync",
      "connect",
      "cloud",
      "another computer",
      "another browser",
    ],
    content: [
      'Inside Settings (gear icon, top right): "Connect" under "Google Drive" links this browser to your Google account, once — not per project. From then on, every project you save here also gets uploaded to Drive in the background, without blocking or waiting on the local save: the browser always wins.',
      "When you connect (or reconnect), the app matches what's already in Drive against your local projects: one that only exists in Drive gets downloaded; one that only exists locally prompts you to upload it; if the same project exists in both places and differs, it flags a conflict (with an option to use the Drive version) instead of overwriting either side outright.",
      "Mainly useful for having the same projects available on more than one computer or browser.",
    ],
  },
  {
    id: "gestion-proyecto-abierto",
    category: "Other",
    title: "Rename, save a copy, delete, export or update the open project",
    keywords: [
      "menu",
      "three dots",
      "rename project",
      "save copy as",
      "delete project",
      "export",
      "ehdz",
      "csv",
      "update from csv",
      "close project",
    ],
    content: [
      'Next to the project name, in the top bar: "Rename project…", and the "⋮" menu with "Save copy as…" (creates a new, independent project, without touching the current one — also the way to turn an example into a real project), "Export copy…", "Update from CSVs…" and "Delete this project" (permanent, disabled while the project is an example without its own copy).',
      '"Export copy…" asks you to choose between two formats: a portable .ehdz copy (only this app can reopen it; meant for moving the project between browsers or computers), or a set of CSV files in a .zip (sub-basins, zones, parameters, aquifers...) to inspect or use with other tools, with a separate report of any incomplete data found while exporting.',
      '"Update from CSVs…" does the opposite: it applies a folder with those same CSV files, edited by hand (e.g. after correcting parameters outside the app), onto the already-open project — matched by name, showing how many sub-basins/zones/aquifers/outlet points would be updated or created before applying anything.',
      '"Close project" (next to the name) saves immediately and returns to the start screen.',
    ],
  },
];
